import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Star, MessageSquare, Send, Filter, ChevronDown, Plus, Lock, Unlock, ExternalLink, Calendar, Download, Edit, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import * as XLSX from 'xlsx';

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { formatDateStandard } from "@servify/shared-utils";
import { useEffect } from "react";

// Schema for category-based feedback (single category submission)
const categoryFeedbackSchema = z.object({
  rating: z.number().min(1, "Please select a rating").max(5, "Rating must be between 1 and 5"),
  comments: z.string().optional(),
  category: z.string().min(1, "Category is required"),
});

type CategoryFeedbackForm = z.infer<typeof categoryFeedbackSchema>;

// Predefined feedback categories for employee induction experience
const FEEDBACK_CATEGORIES = [
  {
    id: "general",
    title: "General Experience",
    description: "Overall onboarding and company experience"
  },
  {
    id: "module",
    title: "Training Modules", 
    description: "Content, structure, and learning experience"
  },
  {
    id: "technical",
    title: "Technical Setup",
    description: "System access, tools, and technical onboarding"
  },
  {
    id: "ui",
    title: "User Interface",
    description: "Platform design, navigation, and usability"
  },
  {
    id: "content",
    title: "Content Quality",
    description: "Information clarity, relevance, and helpfulness"
  },
  {
    id: "performance", 
    title: "System Performance",
    description: "Loading times, responsiveness, and stability"
  }
];

// Helper function to get category info
const getCategoryInfo = (categoryId: string) => {
  return FEEDBACK_CATEGORIES.find(cat => cat.id === categoryId);
};

// Import the interface from the server
interface FeedbackWithUser {
  id: string;
  rating: number;
  comments: string | null;
  category: string | null;
  submittedAt: Date | null;
  userId: string | null;
  user: {
    firstName: string;
    lastName: string;
    companyEmail: string | null;
    personalEmail: string | null;
  } | null;
}

interface FeedbackResponse {
  feedback: FeedbackWithUser[];
}

interface UserFeedbackResponse {
  feedback: FeedbackWithUser[];
}

interface UserResponse {
  user: {
    id: string;
    email: string;
    role: string;
    firstName: string;
    lastName: string;
    dateOfJoining?: string;
  };
}

interface DayWiseFeedbackForm {
  id: number;
  day: number;
  title: string;
  description?: string;
  formUrl?: string;
  isActive: boolean;
  createdAt: string;
}

interface DayWiseFeedbackFormsResponse {
  forms: DayWiseFeedbackForm[];
}

export default function Feedback() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [isExporting, setIsExporting] = useState(false);

  const { data: user } = useQuery<UserResponse>({
    queryKey: ['/auth/me'],
  });

  const { data: allFeedback, isLoading: allFeedbackLoading } = useQuery<FeedbackResponse>({
    queryKey: ['/feedback'],
    enabled: user?.user?.role === 'admin',
  });

  const { data: userFeedback, isLoading: userFeedbackLoading } = useQuery<UserFeedbackResponse>({
    queryKey: ['/feedback/my'],
    enabled: user?.user?.role !== 'admin',
  });

  // Fetch day-wise feedback forms for employees
  const { data: dayWiseForms, isLoading: formsLoading, isError: formsError } = useQuery<DayWiseFeedbackFormsResponse>({
    queryKey: ['/feedback-forms'],
    enabled: user?.user?.role !== 'admin',
  });

  // Fetch all feedback forms for admin
  const { data: adminForms, refetch: refetchAdminForms, isLoading: adminFormsLoading, error: adminFormsError } = useQuery<DayWiseFeedbackFormsResponse>({
    queryKey: ['/feedback-forms'],
    enabled: user?.user?.role === 'admin',
    queryFn: async () => {
      try {
        const response = await apiRequest("GET", "/feedback-forms");
        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`);
        }
        const data = await response.json();
        console.log('📋 Admin forms data:', data);
        return data;
      } catch (error) {
        console.error('❌ Error fetching feedback forms:', error);
        throw error;
      }
    },
  });

  // Debug log
  useEffect(() => {
    if (user?.user?.role === 'admin') {
      console.log('👤 User is admin');
      console.log('📊 adminForms:', adminForms);
      console.log('📊 Forms count:', adminForms?.forms?.length);
      console.log('⏳ Loading:', adminFormsLoading);
      console.log('❌ Error:', adminFormsError);
    }
  }, [adminForms, adminFormsLoading, adminFormsError, user]);

  const [editingForm, setEditingForm] = useState<DayWiseFeedbackForm | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deletingFormId, setDeletingFormId] = useState<number | null>(null);

  const editFormSchema = z.object({
    title: z.string().min(1, "Title is required"),
    description: z.string().optional(),
    day: z.coerce.number().min(1, "Day must be at least 1"),
    formUrl: z.string().url("Please enter a valid URL").optional().or(z.literal("")),
    isActive: z.boolean().default(true),
  });

  const editForm = useForm({
    resolver: zodResolver(editFormSchema),
    defaultValues: {
      title: "",
      description: "",
      day: 1,
      formUrl: "",
      isActive: true,
    },
  });

  const updateFormMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("PUT", `/feedback-forms/${editingForm?.id}`, data);
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Success", description: "Feedback form updated successfully." });
      queryClient.invalidateQueries({ queryKey: ['/feedback-forms'] });
      refetchAdminForms();
      setIsEditDialogOpen(false);
      setEditingForm(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const deleteFormMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest("DELETE", `/feedback-forms/${id}`);
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Success", description: "Feedback form deleted successfully." });
      queryClient.invalidateQueries({ queryKey: ['/feedback-forms'] });
      refetchAdminForms();
      setDeletingFormId(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  useEffect(() => {
    if (editingForm && isEditDialogOpen) {
      editForm.reset({
        title: editingForm.title,
        description: editingForm.description || "",
        day: editingForm.day,
        formUrl: editingForm.formUrl || "",
        isActive: editingForm.isActive,
      });
    } else if (!isEditDialogOpen) {
      editForm.reset();
    }
  }, [editingForm, isEditDialogOpen]);

  // Calculate days elapsed since joining (fixed for timezone consistency)
  const calculateDaysElapsed = (dateOfJoining?: string): number => {
    if (!dateOfJoining) return 0;
    
    // Normalize both dates to start-of-day for consistent calculation
    const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const joinDate = startOf(new Date(dateOfJoining));
    const todayDate = startOf(new Date());
    
    // Calculate days elapsed (1 on joining day, 2 the next day, etc.)
    const daysElapsed = Math.floor((todayDate.getTime() - joinDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    
    // Clamp to 0 if joining is in the future
    return todayDate < joinDate ? 0 : daysElapsed;
  };

  const daysElapsed = calculateDaysElapsed(user?.user?.dateOfJoining);

  // Check if a day-wise form is unlocked based on joining date
  const isFormUnlocked = (formDay: number): boolean => {
    return daysElapsed >= formDay;
  };

  // Get active day-wise forms sorted by day
  const activeForms = dayWiseForms?.forms
    ?.filter((form: DayWiseFeedbackForm) => form.isActive)
    ?.sort((a: DayWiseFeedbackForm, b: DayWiseFeedbackForm) => a.day - b.day) || [];

  // Get submitted categories for current user to prevent duplicates
  const getSubmittedCategories = (): Set<string> => {
    if (!userFeedback?.feedback) return new Set();
    return new Set(
      userFeedback.feedback
        .filter((fb: FeedbackWithUser) => fb.category)
        .map((fb: FeedbackWithUser) => fb.category!)
    );
  };

  const submittedCategories = getSubmittedCategories();

  // Category feedback submission mutation
  const submitCategoryFeedbackMutation = useMutation({
    mutationFn: async (data: CategoryFeedbackForm) => {
      const response = await apiRequest("POST", "/feedback", data);
      return response.json();
    },
    onSuccess: (_, variables) => {
      toast({
        title: "Thank you for your feedback!",
        description: `Your feedback for ${getCategoryInfo(variables.category)?.title} has been submitted successfully.`,
      });
      queryClient.invalidateQueries({ queryKey: ['/feedback'] });
      queryClient.invalidateQueries({ queryKey: ['/feedback/my'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Component for individual category feedback section
  const CategoryFeedbackSection = ({ category }: { category: typeof FEEDBACK_CATEGORIES[0] }) => {
    const [selectedRating, setSelectedRating] = useState(0);
    const isSubmitted = submittedCategories.has(category.id);
    
    const categoryForm = useForm<CategoryFeedbackForm>({
      resolver: zodResolver(categoryFeedbackSchema),
      defaultValues: {
        rating: 0,
        comments: "",
        category: category.id,
      },
    });

    const handleStarClick = (rating: number) => {
      if (isSubmitted) return; // Prevent interaction if already submitted
      setSelectedRating(rating);
      categoryForm.setValue('rating', rating);
      categoryForm.clearErrors('rating');
    };

    const onSubmitCategory = (data: CategoryFeedbackForm) => {
      if (isSubmitted) return; // Prevent submission if already submitted
      submitCategoryFeedbackMutation.mutate({
        ...data,
        rating: selectedRating,
        category: category.id,
      });
    };

    const renderStars = (rating: number, interactive: boolean = false) => {
      return (
        <div className="flex">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type={interactive ? "button" : undefined}
              onClick={interactive ? () => handleStarClick(star) : undefined}
              className={`h-6 w-6 transition-colors ${
                interactive && !isSubmitted ? 'cursor-pointer hover:scale-110' : 'cursor-default'
              } ${
                star <= (interactive ? selectedRating : rating)
                  ? 'text-yellow-400'
                  : 'text-gray-300'
              }`}
              disabled={!interactive || isSubmitted}
            >
              <Star className={`h-6 w-6 ${star <= rating ? 'fill-current' : ''}`} />
            </button>
          ))}
        </div>
      );
    };

    return (
      <Card className={`border-2 transition-all ${
        isSubmitted 
          ? 'border-green-200 bg-green-50' 
          : 'border-servify-light-grey hover:border-servify-purple/30'
      }`}>
        <CardHeader className="border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className={`text-lg ${isSubmitted ? 'text-green-700' : 'text-servify-dark-grey'}`}>
                {category.title}
              </CardTitle>
              <p className={`text-sm ${isSubmitted ? 'text-green-600' : 'text-servify-medium-grey'}`}>
                {category.description}
              </p>
            </div>
            {isSubmitted && (
              <div className="flex items-center text-green-600">
                <MessageSquare className="h-5 w-5 mr-1" />
                <span className="text-sm font-medium">Submitted</span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {isSubmitted ? (
            <div className="text-center py-6">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <MessageSquare className="h-6 w-6 text-green-600" />
              </div>
              <p className="text-green-700 font-medium">Thank you for your feedback!</p>
              <p className="text-green-600 text-sm">You have already submitted feedback for this category.</p>
            </div>
          ) : (
            <Form {...categoryForm}>
              <form onSubmit={categoryForm.handleSubmit(onSubmitCategory)} className="space-y-6">
                {/* Rating Section */}
                <FormField
                  control={categoryForm.control}
                  name="rating"
                  render={() => (
                    <FormItem>
                      <FormLabel className="text-base font-semibold">
                        How would you rate this aspect of your induction?
                      </FormLabel>
                      <FormControl>
                        <div className="flex items-center space-x-2">
                          {renderStars(selectedRating, true)}
                          {selectedRating > 0 && (
                            <span className="ml-3 text-sm font-medium text-servify-dark-grey">
                              {selectedRating === 1 && "Poor"}
                              {selectedRating === 2 && "Fair"} 
                              {selectedRating === 3 && "Good"}
                              {selectedRating === 4 && "Very Good"}
                              {selectedRating === 5 && "Excellent"}
                            </span>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Comments Section */}
                <FormField
                  control={categoryForm.control}
                  name="comments"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-semibold">
                        Comments or Suggestions <span className="text-sm font-normal text-servify-medium-grey">(Optional)</span>
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder={`Share any specific feedback about ${category.title.toLowerCase()}...`}
                          className="min-h-[100px] border-2 border-servify-light-grey focus:border-servify-purple"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Submit Button */}
                <Button 
                  type="submit" 
                  className="w-full bg-servify-purple hover:bg-servify-purple/90 text-white"
                  disabled={submitCategoryFeedbackMutation.isPending || selectedRating === 0}
                >
                  {submitCategoryFeedbackMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit Feedback for {category.title}
                    </>
                  )}
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    );
  };

  // Non-interactive star renderer for display purposes only
  const renderStars = (rating: number, size: string = "h-6 w-6") => {
    return (
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} ${
              star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
            data-testid={`star-${star}`}
          />
        ))}
      </div>
    );
  };

  const averageRating = allFeedback?.feedback && allFeedback.feedback.length > 0 
    ? allFeedback.feedback.reduce((acc: number, item: FeedbackWithUser) => acc + item.rating, 0) / allFeedback.feedback.length 
    : 0;

  const categoryFilter = (feedback: FeedbackWithUser[], category: string) => {
    if (category === 'all') return feedback;
    return feedback.filter(item => item.category === category);
  };

  // Function to export feedback to Excel
  const exportToExcel = () => {
    try {
      setIsExporting(true);
      
      if (!allFeedback?.feedback || allFeedback.feedback.length === 0) {
        toast({
          title: "No Data",
          description: "No feedback data available to export.",
          variant: "destructive",
        });
        setIsExporting(false);
        return;
      }

      // Create workbook
      const workbook = XLSX.utils.book_new();

      // Sheet 1: All Feedback Details
      const feedbackData = allFeedback.feedback.map((item: FeedbackWithUser) => ({
        'ID': item.id,
        'User Name': item.user ? `${item.user.firstName} ${item.user.lastName}` : 'N/A',
        'Email': item.user ? (item.user.companyEmail || item.user.personalEmail || 'N/A') : 'N/A',
        'Rating': item.rating,
        'Category': item.category || 'General',
        'Comments': item.comments || 'No comments provided',
        'Submitted Date': item.submittedAt ? formatDateStandard(new Date(item.submittedAt)) : 'N/A',
        'Submitted DateTime': item.submittedAt ? new Date(item.submittedAt).toLocaleString() : 'N/A',
      }));
      
      const feedbackSheet = XLSX.utils.json_to_sheet(feedbackData);
      XLSX.utils.book_append_sheet(workbook, feedbackSheet, 'All Feedback');

      // Sheet 2: Feedback Summary by Category
      const categorySummary: Record<string, { count: number; totalRating: number; avgRating: number }> = {};
      
      allFeedback.feedback.forEach((item: FeedbackWithUser) => {
        const category = item.category || 'General';
        if (!categorySummary[category]) {
          categorySummary[category] = { count: 0, totalRating: 0, avgRating: 0 };
        }
        categorySummary[category].count++;
        categorySummary[category].totalRating += item.rating;
      });

      const summaryData = Object.entries(categorySummary).map(([category, stats]) => ({
        'Category': category,
        'Total Feedback': stats.count,
        'Average Rating': (stats.totalRating / stats.count).toFixed(2),
        'Total Rating Points': stats.totalRating,
      }));

      // Add overall summary row
      summaryData.push({
        'Category': 'Overall',
        'Total Feedback': allFeedback.feedback.length,
        'Average Rating': averageRating.toFixed(2),
        'Total Rating Points': allFeedback.feedback.reduce((sum: number, item: FeedbackWithUser) => sum + item.rating, 0),
      });

      const summarySheet = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary by Category');

      // Sheet 3: Rating Distribution
      const ratingDistribution: Record<number, number> = {};
      allFeedback.feedback.forEach((item: FeedbackWithUser) => {
        ratingDistribution[item.rating] = (ratingDistribution[item.rating] || 0) + 1;
      });

      const distributionData = [1, 2, 3, 4, 5].map((rating) => ({
        'Rating': rating,
        'Count': ratingDistribution[rating] || 0,
        'Percentage': allFeedback.feedback.length > 0 
          ? ((ratingDistribution[rating] || 0) / allFeedback.feedback.length * 100).toFixed(2) + '%'
          : '0%',
      }));

      const distributionSheet = XLSX.utils.json_to_sheet(distributionData);
      XLSX.utils.book_append_sheet(workbook, distributionSheet, 'Rating Distribution');

      // Generate filename with current date
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `Feedback_Export_${dateStr}.xlsx`;

      // Download the file
      XLSX.writeFile(workbook, filename);

      toast({
        title: "Export Successful!",
        description: `Feedback data exported to ${filename}`,
      });
      
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast({
        title: "Export Failed",
        description: "Failed to export feedback data to Excel. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (user?.user?.role === 'admin') {
    return (
      <div className="space-y-6" data-testid="admin-feedback-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-servify-dark-grey" data-testid="text-feedback-title">Feedback Management</h1>
            <p className="text-servify-medium-grey mt-1" data-testid="text-feedback-description">
              Review and analyze feedback from employees
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={exportToExcel}
              disabled={isExporting || !allFeedback?.feedback || allFeedback.feedback.length === 0}
              className="bg-servify-green hover:bg-servify-green/90 text-white"
              data-testid="button-export-feedback"
            >
              <Download className="h-4 w-4 mr-2" />
              {isExporting ? 'Exporting...' : 'Export to Excel'}
            </Button>
            <Button
              onClick={() => setLocation('/admin/add-feedback-form')}
              className="bg-servify-purple hover:bg-servify-purple/90 text-white"
              data-testid="button-add-feedback-form"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Feedback Form
            </Button>
          </div>
        </div>

        {/* Feedback Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="feedback-stats">
          <Card className="border-servify-light-grey shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-servify-purple/10 rounded-lg flex items-center justify-center">
                  <Star className="text-servify-purple h-6 w-6" />
                </div>
                <span className="text-2xl font-bold text-servify-purple" data-testid="text-average-rating">
                  {averageRating.toFixed(1)}
                </span>
              </div>
              <h3 className="font-semibold text-servify-dark-grey mb-1">Average Rating</h3>
              <div className="flex items-center">
                {renderStars(Math.round(averageRating))}
                <span className="ml-2 text-sm text-servify-medium-grey">({allFeedback?.feedback?.length || 0} reviews)</span>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-servify-light-grey shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-servify-purple/10 rounded-lg flex items-center justify-center">
                  <MessageSquare className="text-servify-purple h-6 w-6" />
                </div>
                <span className="text-2xl font-bold text-servify-purple" data-testid="text-total-feedback">
                  {allFeedback?.feedback?.length || 0}
                </span>
              </div>
              <h3 className="font-semibold text-servify-dark-grey mb-1">Total Feedback</h3>
              <p className="text-servify-medium-grey text-sm">All time submissions</p>
            </CardContent>
          </Card>

          <Card className="border-servify-light-grey shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-servify-purple/10 rounded-lg flex items-center justify-center">
                  <Filter className="text-servify-purple h-6 w-6" />
                </div>
                <span className="text-2xl font-bold text-servify-purple" data-testid="text-categories">
                  {allFeedback?.feedback ? new Set(allFeedback.feedback.map((f: FeedbackWithUser) => f.category).filter(Boolean)).size : 0}
                </span>
              </div>
              <h3 className="font-semibold text-servify-dark-grey mb-1">Categories</h3>
              <p className="text-servify-medium-grey text-sm">Feedback types</p>
            </CardContent>
          </Card>
        </div>

        {/* Feedback List */}
        <Card data-testid="feedback-list" className="border-servify-light-grey shadow-lg">
          <CardHeader className="border-b border-servify-light-grey">
            <CardTitle>All Feedback</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {allFeedbackLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse p-4 border border-servify-light-grey rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="h-4 bg-servify-light-grey rounded w-24"></div>
                      <div className="h-4 bg-servify-light-grey rounded w-16"></div>
                    </div>
                    <div className="h-4 bg-servify-white-grey rounded mb-2"></div>
                    <div className="h-4 bg-servify-white-grey rounded w-3/4"></div>
                  </div>
                ))}
              </div>
            ) : allFeedback?.feedback && allFeedback.feedback.length > 0 ? (
              <div className="space-y-4">
                {allFeedback.feedback.map((item: FeedbackWithUser) => (
                  <div key={item.id} className="p-4 border border-servify-light-grey rounded-lg hover:border-servify-purple/30 transition-colors" data-testid={`feedback-item-${item.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        {renderStars(item.rating)}
                        <span className="px-2 py-1 bg-servify-purple/10 text-servify-purple rounded-full text-xs font-medium" data-testid={`feedback-category-${item.id}`}>
                          {item.category || 'General'}
                        </span>
                      </div>
                      <span className="text-sm text-servify-medium-grey" data-testid={`feedback-date-${item.id}`}>
                        {item.submittedAt ? formatDateStandard(new Date(item.submittedAt)) : 'N/A'}
                      </span>
                    </div>
                    <p className="text-servify-dark-grey" data-testid={`feedback-comments-${item.id}`}>
                      {item.comments || 'No comments provided.'}
                    </p>
                    {item.user && (
                      <p className="text-sm text-servify-medium-grey mt-2" data-testid={`feedback-author-${item.id}`}>
                        — {item.user.firstName} {item.user.lastName}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12" data-testid="no-feedback-message">
                <MessageSquare className="h-16 w-16 text-servify-light-grey mx-auto mb-4" />
                <h3 className="text-lg font-medium text-servify-dark-grey mb-2">No Feedback Yet</h3>
                <p className="text-servify-medium-grey">Feedback from employees will appear here once submitted.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Feedback Forms Management */}
        <Card className="border-servify-light-grey shadow-lg mt-6">
          <CardHeader className="border-b border-servify-light-grey">
            <CardTitle className="text-xl font-bold text-servify-dark-grey">Feedback Forms Management</CardTitle>
            <p className="text-sm text-servify-medium-grey mt-1">View, edit, and delete all feedback forms</p>
          </CardHeader>
          <CardContent className="p-6">
            {adminFormsLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-servify-purple mx-auto mb-4"></div>
                <p className="text-servify-medium-grey">Loading feedback forms...</p>
              </div>
            ) : adminFormsError ? (
              <div className="text-center py-12">
                <MessageSquare className="h-16 w-16 text-red-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-servify-dark-grey mb-2">Error Loading Forms</h3>
                <p className="text-servify-medium-grey mb-4">{(adminFormsError as any)?.message || 'Failed to load feedback forms'}</p>
                <Button
                  onClick={() => refetchAdminForms()}
                  variant="outline"
                  className="border-servify-purple text-servify-purple"
                >
                  Retry
                </Button>
              </div>
            ) : adminForms?.forms && Array.isArray(adminForms.forms) && adminForms.forms.length > 0 ? (
              <div className="space-y-4">
                {adminForms.forms.map((form: DayWiseFeedbackForm) => (
                  <div key={form.id} className="p-5 border-2 border-servify-light-grey rounded-lg hover:border-servify-purple/30 transition-all shadow-sm hover:shadow-md bg-white">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                          <h3 className="font-semibold text-lg text-servify-dark-grey">{form.title}</h3>
                          <span className="px-3 py-1 bg-servify-purple/10 text-servify-purple rounded-full text-xs font-medium whitespace-nowrap">
                            Day {form.day}
                          </span>
                          {form.isActive ? (
                            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium whitespace-nowrap">Active</span>
                          ) : (
                            <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium whitespace-nowrap">Inactive</span>
                          )}
                        </div>
                        {form.description && (
                          <p className="text-sm text-servify-medium-grey mb-2 line-clamp-2">{form.description}</p>
                        )}
                        {form.formUrl && (
                          <a 
                            href={form.formUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-sm text-servify-purple hover:underline flex items-center gap-1 mb-2"
                          >
                            <ExternalLink className="h-3 w-3" />
                            View Form
                          </a>
                        )}
                        <p className="text-xs text-servify-medium-grey">
                          Created: {formatDateStandard(new Date(form.createdAt))}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingForm(form);
                            setIsEditDialogOpen(true);
                          }}
                          className="border-servify-purple text-servify-purple hover:bg-servify-purple hover:text-white transition-colors"
                        >
                          <Edit className="h-4 w-4 mr-1.5" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeletingFormId(form.id)}
                          disabled={deleteFormMutation.isPending}
                          className="border-red-500 text-red-600 hover:bg-red-50 hover:border-red-600 transition-colors"
                        >
                          <Trash2 className="h-4 w-4 mr-1.5" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Calendar className="h-16 w-16 text-servify-light-grey mx-auto mb-4" />
                <h3 className="text-lg font-medium text-servify-dark-grey mb-2">No Feedback Forms</h3>
                <p className="text-servify-medium-grey mb-4">Create your first feedback form to get started.</p>
                <Button
                  onClick={() => setLocation('/admin/add-feedback-form')}
                  className="bg-servify-purple hover:bg-servify-purple-dark text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Feedback Form
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) {
            setEditingForm(null);
            editForm.reset();
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white z-[100]">
            <DialogHeader className="pb-4 border-b border-servify-light-grey">
              <DialogTitle className="text-2xl font-bold text-servify-dark-grey">Edit Feedback Form</DialogTitle>
              <DialogDescription className="text-servify-medium-grey mt-2">Update the feedback form details below.</DialogDescription>
            </DialogHeader>
            <Form {...editForm}>
              <form onSubmit={editForm.handleSubmit((data) => updateFormMutation.mutate(data))} className="space-y-6 mt-6">
                <FormField
                  control={editForm.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-semibold text-servify-dark-grey">Title *</FormLabel>
                      <FormControl>
                        <Input 
                          {...field} 
                          className="h-12 border-2 border-servify-light-grey focus:border-servify-purple focus:ring-2 focus:ring-servify-purple/20"
                          placeholder="Enter form title"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-semibold text-servify-dark-grey">Description <span className="text-sm font-normal text-servify-medium-grey">(Optional)</span></FormLabel>
                      <FormControl>
                        <Textarea 
                          {...field} 
                          className="min-h-[100px] border-2 border-servify-light-grey focus:border-servify-purple focus:ring-2 focus:ring-servify-purple/20"
                          placeholder="Enter form description"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={editForm.control}
                    name="day"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base font-semibold text-servify-dark-grey">Day *</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            {...field}
                            className="h-12 border-2 border-servify-light-grey focus:border-servify-purple focus:ring-2 focus:ring-servify-purple/20"
                            placeholder="Day number"
                            min="1"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={editForm.control}
                    name="formUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base font-semibold text-servify-dark-grey">Form URL <span className="text-sm font-normal text-servify-medium-grey">(Optional)</span></FormLabel>
                        <FormControl>
                          <Input 
                            {...field} 
                            className="h-12 border-2 border-servify-light-grey focus:border-servify-purple focus:ring-2 focus:ring-servify-purple/20"
                            placeholder="https://..."
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="flex justify-end gap-3 pt-6 border-t border-servify-light-grey mt-6">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setIsEditDialogOpen(false);
                      setEditingForm(null);
                      editForm.reset();
                    }}
                    className="border-servify-light-grey hover:bg-servify-white-grey min-w-[100px]"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={updateFormMutation.isPending} 
                    className="bg-servify-purple hover:bg-servify-purple-dark text-white min-w-[120px]"
                  >
                    {updateFormMutation.isPending ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                        Updating...
                      </>
                    ) : (
                      "Update Form"
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deletingFormId !== null} onOpenChange={(open) => !open && setDeletingFormId(null)}>
          <AlertDialogContent className="max-w-md bg-white">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-xl font-bold text-servify-dark-grey">Delete Feedback Form</AlertDialogTitle>
              <AlertDialogDescription className="text-servify-medium-grey mt-2">
                Are you sure you want to delete this feedback form? This action cannot be undone and will remove the form from the system.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-3 mt-6">
              <AlertDialogCancel className="border-servify-light-grey hover:bg-servify-white-grey min-w-[100px]">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (deletingFormId) {
                    deleteFormMutation.mutate(deletingFormId);
                  }
                }}
                disabled={deleteFormMutation.isPending}
                className="bg-red-600 hover:bg-red-700 text-white min-w-[100px]"
              >
                {deleteFormMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // Employee/Candidate feedback submission form
  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="employee-feedback-page">
      <div className="text-left">
        <h1 className="text-3xl font-bold text-servify-dark-grey" data-testid="text-submit-feedback-title">Share Your Feedback</h1>
        <p className="text-servify-medium-grey mt-2" data-testid="text-submit-feedback-description">
          Help us improve the induction experience for future employees
        </p>
      </div>

      {/* User's Own Feedback History */}
      {userFeedback?.feedback && userFeedback.feedback.length > 0 && (
        <Card className="border-servify-light-grey shadow-lg">
          <CardHeader className="border-b border-servify-light-grey">
            <CardTitle className="flex items-center">
              <MessageSquare className="mr-2 h-5 w-5 text-servify-purple" />
              Your Feedback History
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {userFeedbackLoading ? (
              <div className="text-center py-4">Loading your feedback...</div>
            ) : (
              <div className="space-y-4">
                {userFeedback.feedback.map((item: FeedbackWithUser) => (
                  <div key={item.id} className="p-4 border border-servify-light-grey rounded-lg bg-servify-white-grey">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        {renderStars(item.rating)}
                        <span className="px-2 py-1 bg-servify-purple/10 text-servify-purple rounded-full text-xs font-medium">
                          {item.category || 'General'}
                        </span>
                      </div>
                      <span className="text-sm text-servify-medium-grey">
                        {item.submittedAt ? formatDateStandard(new Date(item.submittedAt)) : 'N/A'}
                      </span>
                    </div>
                    <p className="text-servify-dark-grey">
                      {item.comments || 'No comments provided.'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Day-wise Feedback Forms - Always show for employees */}
      <Card className="border-servify-light-grey shadow-lg">
        <CardHeader className="border-b border-servify-light-grey">
          <CardTitle className="flex items-center">
            <Calendar className="mr-2 h-5 w-5 text-servify-purple" />
            Day-wise Feedback Forms
          </CardTitle>
          {!formsLoading && !formsError && (
            <p className="text-sm text-servify-medium-grey">
              You've been with us for {daysElapsed} day{daysElapsed !== 1 ? 's' : ''}. Forms unlock progressively based on your joining date.
            </p>
          )}
        </CardHeader>
        <CardContent className="p-6">
          {formsLoading ? (
            <div className="text-center py-4">Loading feedback forms...</div>
          ) : formsError ? (
            <div className="text-center py-8">
              <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">Unable to load feedback forms</h3>
              <p className="text-gray-500">Please try refreshing the page or contact support if the issue persists.</p>
            </div>
          ) : activeForms.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No feedback forms available</h3>
              <p className="text-gray-500">Your admin hasn't created any day-wise feedback forms yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeForms.map((form: DayWiseFeedbackForm) => {
                const isUnlocked = isFormUnlocked(form.day);
                return (
                  <div
                    key={form.id}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                      isUnlocked
                        ? 'border-servify-purple/30 bg-white hover:border-servify-purple/60 hover:shadow-md'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            isUnlocked
                              ? 'bg-servify-purple text-white'
                              : 'bg-gray-300 text-gray-600'
                          }`}
                        >
                          {form.day}
                        </div>
                        {isUnlocked ? (
                          <Unlock className="h-4 w-4 text-green-500" />
                        ) : (
                          <Lock className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        isUnlocked 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        Day {form.day}
                      </span>
                    </div>
                    
                    <h4 className={`font-semibold mb-2 ${
                      isUnlocked ? 'text-servify-dark-grey' : 'text-gray-500'
                    }`}>
                      {form.title}
                    </h4>
                    
                    {form.description && (
                      <p className={`text-sm mb-3 ${
                        isUnlocked ? 'text-servify-medium-grey' : 'text-gray-400'
                      }`}>
                        {form.description}
                      </p>
                    )}
                    
                    {isUnlocked ? (
                      <Button
                        onClick={() => form.formUrl && window.open(form.formUrl, '_blank')}
                        disabled={!form.formUrl}
                        className="w-full bg-servify-purple hover:bg-servify-purple/90 text-white"
                        size="sm"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Open Form
                      </Button>
                    ) : (
                      <div className="text-center">
                        <p className="text-xs text-gray-400 mb-2">
                          Unlocks in {Math.max(0, form.day - daysElapsed)} day{Math.max(0, form.day - daysElapsed) !== 1 ? 's' : ''}
                        </p>
                        <Button
                          disabled
                          className="w-full bg-gray-200 text-gray-400 cursor-not-allowed"
                          size="sm"
                        >
                          <Lock className="h-4 w-4 mr-2" />
                          Locked
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Category-based Feedback Sections */}
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-servify-dark-grey mb-2">Induction Experience Feedback</h2>
          <p className="text-servify-medium-grey">
            Please provide your feedback for each aspect of your induction experience. You can submit feedback for each category independently.
          </p>
          <div className="mt-4 flex items-center justify-center space-x-4 text-sm">
            <div className="flex items-center text-green-600">
              <MessageSquare className="h-4 w-4 mr-1" />
              <span>{submittedCategories.size} of {FEEDBACK_CATEGORIES.length} completed</span>
            </div>
          </div>
        </div>

        {FEEDBACK_CATEGORIES.map((category) => (
          <CategoryFeedbackSection key={category.id} category={category} />
        ))}
      </div>
    </div>
  );
}